// FEAT-34 — the plan wall, as the browser sees it.
//
// The SERVER holds the wall since 2026-10-05: a workspace that must pick a
// plan (`needs_plan` — its free trial ran out, or it never had one) gets
// 402 `PLAN_REQUIRED` on every recruiter route but the few
// `backend/entitlements.py::PLAN_WALL_OPEN` names. This module only answers
// the two questions the UI asks about it; it grants nothing.
//
// No imports on purpose: `lib/api.ts` reads `isPlanRequired` on every admin
// response, and `RequirePlan` + its test read `planWallCovers`.

export const PLAN_REQUIRED_CODE = "PLAN_REQUIRED";

/** Is this response the plan wall's refusal — 402 + `detail.code`? */
export function isPlanRequired(status: number, detail: unknown): boolean {
  if (status !== 402) return false;
  if (!detail || typeof detail !== "object") return false;
  return (detail as { code?: unknown }).code === PLAN_REQUIRED_CODE;
}

/** The server's own sentence on a `PLAN_REQUIRED` 402, for an error message
 *  a person reads — never the raw `API 402: {json}`. */
export function planRequiredMessage(detail: unknown): string {
  const message =
    detail && typeof detail === "object"
      ? (detail as { message?: unknown }).message
      : undefined;
  return typeof message === "string" && message.trim()
    ? message
    : "This workspace needs a plan.";
}

/** The plan wall's sentence when `err` is its refusal (an `ApiError`-shaped
 *  402 PLAN_REQUIRED), else null. For the Team page's mappers, which turn
 *  every other code into their own copy: the actions it mounts behind the
 *  wall (invite, resend, edit, access, reset) are product and stay walled,
 *  so they must say WHY rather than "please try again" (verify 2026-10-07,
 *  S14). Duck-typed — this module imports nothing. */
export function planWallError(err: unknown): string | null {
  if (!err || typeof err !== "object") return null;
  const { status, detail } = err as { status?: unknown; detail?: unknown };
  return typeof status === "number" && isPlanRequired(status, detail)
    ? planRequiredMessage(detail)
    : null;
}

/**
 * The cached session as the server's 402 just described it: the workspace
 * must pick a plan — and, if a trial was showing, that trial has ended (only
 * an ended trial walls a company that had one). Applied the moment the first
 * 402 lands, BEFORE the page's own query can retry and render its "couldn't
 * load" card; the refetch that follows replaces it with the server's copy.
 * FEAT-34 verify, 2026-10-06.
 */
export function markWalled<
  T extends {
    needs_plan?: boolean;
    trial?: { state: string; days_left: number } | null;
  },
>(session: T): T {
  return {
    ...session,
    needs_plan: true,
    trial: session.trial
      ? { ...session.trial, state: "ended", days_left: 0 }
      : session.trial,
  };
}

/** How often a walled tab re-asks for the session, so a plan granted or
 *  bought elsewhere (an operator's grant, checkout in another tab) lifts the
 *  wall without a reload. Only while walled; also on window focus. */
export const WALLED_SESSION_POLL_MS = 30_000;

/**
 * Does the wall replace the page at this path?
 *
 * Everything under /admin except Billing — it carries the same plan picker
 * plus the plan and invoice detail, so walling it would strand the very
 * person the wall is asking to pay — and Support, whose every read the
 * server leaves open (`PLAN_WALL_OPEN`): a walled workspace must still read
 * our replies (pre-ship 2026-10-07, R2) — and Team, where offboarding
 * lives: removing a member and voiding an invitation are "turning a setting
 * OFF", which the server leaves open too (pre-ship 2026-10-07, S14; inviting
 * stays walled and toasts the server's sentence). `AdminLayout` applies the
 * same rule to the content it wraps; `RequirePlan` applies it one level up,
 * so a walled page is never MOUNTED and its queries never ask the server
 * for a 402.
 */
export function planWallCovers(pathname: string): boolean {
  return !(
    pathname.startsWith("/admin/billing") ||
    pathname.startsWith("/admin/support") ||
    pathname.startsWith("/admin/team")
  );
}

/**
 * The page a wall rose UNDER — the trial ended while it was open — stays
 * MOUNTED (its `AdminLayout` hides it behind the wall) so unsaved work
 * survives a plan bought in another tab (pre-ship 2026-10-07, R8). Returns the
 * pathname to keep, or null: set on the render the wall rises, kept while the
 * path is unchanged, dropped on any navigation. A wall already up when the
 * page opened keeps nothing — that page is never mounted at all.
 */
export function keptPageUnderWall(
  kept: string | null,
  s: { walled: boolean; wasWalled: boolean; pathname: string },
): string | null {
  if (!s.walled) return kept;
  if (kept === null) return s.wasWalled ? null : s.pathname;
  return kept === s.pathname ? kept : null;
}

